import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { useTheme } from '@emotion/react';
import { fromTheme } from '@kyruus/ui-theme';
import { MapContainer } from '../ProviderMapContainer/styles';
import _debounce from 'lodash/debounce';

export const ResizableMapSection = ({ children, ...props }) => {
  const mapContainerRef = useRef(null);
  const mapContainerHeight = useRef(null);
  const theme = useTheme();
  const spacing_large = fromTheme('spacing_large')({ theme });
  const MAP_SPACING_BOTTOM = parseInt(spacing_large) || 24; // Since the height is being calculated based on the available space, we need to add a margin when the map is sticky

  const computeMapContainerHeight = useCallback((node) => {
    if (typeof window === 'undefined') return;

    const calculatedHeight =
      window.innerHeight - node.getBoundingClientRect().y;
    mapContainerHeight.current = calculatedHeight - MAP_SPACING_BOTTOM;
    mapContainerRef.current.style.height =
      calculatedHeight - MAP_SPACING_BOTTOM + 'px';
  }, []);

  const measuredRef = useCallback(
    (node) => {
      if (node) {
        mapContainerRef.current = node;
        computeMapContainerHeight(node);
      }
    },
    [computeMapContainerHeight]
  );

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const mapResizeHandler = () => {
      if (!mapContainerRef.current) {
        return;
      }

      const bounds = mapContainerRef.current.getBoundingClientRect();
      if (bounds.y <= MAP_SPACING_BOTTOM) {
        mapContainerHeight.current = null;
        return;
      }
      computeMapContainerHeight(mapContainerRef.current);
    };

    const abortController = new AbortController();

    window.addEventListener('scroll', _debounce(mapResizeHandler), {
      signal: abortController.signal
    });

    window.addEventListener('resize', _debounce(mapResizeHandler), {
      signal: abortController.signal
    });

    return () => {
      abortController.abort();
    };
  }, [computeMapContainerHeight]);

  return (
    <MapContainer ref={measuredRef} {...props}>
      {children}
    </MapContainer>
  );
};
